// =============================================================================
// Pintravel — Links from outside the app
// =============================================================================
// A universal link is the same https URL as the website. iOS and Android hand
// it to the app when it is installed and to the browser when it is not, which
// is what lets one link in an email work for everybody.
//
// The app never actually navigates to that URL — the WebView runs on
// capacitor://localhost, and loading an https address would replace the whole
// app with the website. Only the path is taken, and the in-app router is asked
// to go there.
//
// Deliberately allow-listed rather than "route whatever arrives". A link is
// something a stranger can put in front of the user, so the set of paths it may
// reach is a decision, not a consequence of the URL parser.
// =============================================================================

import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * Paths a link may open, matched against the URL's pathname.
 *
 * Reset and sign-up are here because emails link to them: a password reset,
 * and a trip invitation for someone without an account. Everything else an
 * email might point at is behind a login the app already handles.
 */
const ROUTABLE = [
  /^\/reset-password$/,
  /^\/register$/,
  /^\/trips\/[0-9a-f-]{36}$/i,
  /^\/i\/[0-9a-f-]{36}$/i,
  /^\/p\/[0-9a-f-]{36}$/i,
  /^\/u\/[a-z0-9_.]{3,30}$/i,
] as const;

function isRoutable(pathname: string): boolean {
  return ROUTABLE.some((pattern) => pattern.test(pathname));
}

/**
 * Turns an incoming URL into an in-app destination, or null if it is not one we
 * accept.
 *
 * Exported for the tests: the interesting cases are all about what is refused.
 */
export function destinationFor(rawUrl: string, allowedHosts: string[]): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  // A universal link always arrives over https from a host we claimed. Anything
  // else reaching this listener is a custom-scheme link we did not ask for.
  if (url.protocol !== 'https:') return null;
  if (!allowedHosts.includes(url.hostname)) return null;
  if (!isRoutable(url.pathname)) return null;

  // Path, query and hash only — never the origin. Handing the router an
  // absolute URL is how an app ends up navigating itself off itself.
  return `${url.pathname}${url.search}${url.hash}`;
}

/** How long the page waits for the app to take over before carrying on in the web. */
const APP_HANDOFF_MS = 1500;

/**
 * "Open in the app" on a public page. A link to this same site never opens the
 * app (iOS keeps same-domain navigation in Safari), so a phone tries the app's
 * scheme; if the page is still showing a moment later, no app took it and the
 * web carries on with `fallback`. Without an app iOS first says it cannot open
 * the page. Computers, and the app's own web view, go straight to `fallback`.
 */
export function openInApp(path: string, fallback: () => void): void {
  const phone = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!phone || Capacitor.isNativePlatform() || !isRoutable(path)) {
    fallback();
    return;
  }
  window.setTimeout(() => {
    if (document.visibilityState === 'visible') fallback();
  }, APP_HANDOFF_MS);
  window.location.href = `pintravel://open${path}`;
}

/** Hosts whose links belong to this app. Must match the AASA and the entitlement. */
const ALLOWED_HOSTS = ['web.pintravel.app'];

/**
 * Starts listening. Safe to call on the web, where it does nothing — there is
 * no plugin, and a browser opening the URL directly is just navigation.
 */
export function initializeDeepLinks(): void {
  if (!Capacitor.isNativePlatform()) return;

  void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    const destination = destinationFor(event.url, ALLOWED_HOSTS);
    if (!destination) return;

    // Imported here rather than at the top: the router builds a browser history
    // the moment it is loaded, which needs a document. Keeping it behind the
    // listener leaves the URL parsing above testable on its own.
    void import('@/app/router').then(({ router }) => router.navigate(destination));
  });
}

export default initializeDeepLinks;
